import { useState, useEffect, useCallback } from "react";
import { useFormDataStore, useFormStateStore } from "@/components/context/useFormContext";
import { RadioGroupField } from "@/components/ui/radioGroupField";
import clsx from "clsx";
import { LOAN_PURPOSE_BUY_A_HOME, LOAN_PURPOSE_REFINANCE_MORTGAGE } from "@/lib/constants";

export const PropertyDetails = () => {
	const { formValues, setFormValue, clearFormError, setFormError } = useFormDataStore();
	const {
		selectedIndex,
		enabledFields,
		setFormErrors,
		setValidateFormDetails,
		formContainer,
		hasAttemptedSubmit,
	} = useFormStateStore();

	const [isNavDivSmall, setIsNavDivSmall] = useState(false);

	// For form container
	useEffect(() => {
		const limiter = selectedIndex === 0 ? 350 : 460;

		setIsNavDivSmall(formContainer < limiter);
	}, [selectedIndex, formContainer]);

	const { purchaseStage, assetType, propertyPurpose, refinancePurpose } = formValues;

	// Local state for form validation errors
	const [errors, setErrors] = useState({
		purchaseStage: false,
		assetType: false,
		propertyPurpose: false,
		refinancePurpose: false,
	});

	// Checks whether any of the fields have errors. If it does, zustand's formError is set to true
	useEffect(() => {
		const hasError = Object.values(errors).some((error) => error);
		setFormErrors(hasError);
	}, [errors, setFormErrors]);

	// Function to validate all fields
	const validateForm = () => {
		const isBuying = formValues.loanPurpose === LOAN_PURPOSE_BUY_A_HOME;
		const isRefinancing = formValues.loanPurpose === LOAN_PURPOSE_REFINANCE_MORTGAGE;

		const newErrors = {
			// LOAN_PURPOSE_BUY_A_HOME
			purchaseStage: isBuying ? !purchaseStage || purchaseStage === "" : false,
			assetType: isBuying ? !assetType || assetType === "" : false,

			// LOAN_PURPOSE_REFINANCE_MORTGAGE
			propertyPurpose: isRefinancing ? !propertyPurpose || propertyPurpose === "" : false,
			refinancePurpose: isRefinancing ? !refinancePurpose || refinancePurpose === "" : false,
		};

		setErrors(newErrors);

		const hasError = Object.values(newErrors).some(Boolean);
		setFormErrors(hasError);

		return !hasError;
	};
	// Store the validation function in Zustand so QuickForm can call it
	useEffect(() => {
		setValidateFormDetails(validateForm);
	}, [purchaseStage, assetType, propertyPurpose, refinancePurpose]);

	// Reset formValues where it's not enabled.
	useEffect(() => {
		const fieldsToReset: (keyof typeof formValues)[] = [
			"purchaseStage",
			"assetType",
			"propertyPurpose",
			"refinancePurpose",
		];

		fieldsToReset.forEach((field) => {
			if (enabledFields[field]?.length === 0) {
				setFormValue(field, ""); // Reset value when no options are available
				setErrors((prevErrors) => ({
					...prevErrors,
					field: false,
				}));
			}
		});
	}, [enabledFields, setFormValue, setErrors]);

	const handlePurchaseStageChange = useCallback(
		(value: string) => {
			setFormValue("purchaseStage", value);
			setErrors((prevErrors) => ({
				...prevErrors,
				purchaseStage: !value, // Clear error when a selection is made
			}));
		},
		[setFormValue, setFormError, setErrors, clearFormError],
	);

	const handleAssetTypeChange = useCallback(
		(value: string) => {
			setFormValue("assetType", value);
			setErrors((prevErrors) => ({
				...prevErrors,
				assetType: !value, // Clear error when a selection is made
			}));
		},
		[setFormValue, setFormError, setErrors, clearFormError],
	);

	const handlePropertyPurposeChange = useCallback(
		(value: string) => {
			setFormValue("propertyPurpose", value);
			setErrors((prevErrors) => ({
				...prevErrors,
				propertyPurpose: !value, // Clear error when a selection is made
			}));
		},
		[setFormValue, setFormError, setErrors, clearFormError],
	);

	const handleRefinancePurposeChange = useCallback(
		(value: string) => {
			setFormValue("refinancePurpose", value);
			setErrors((prevErrors) => ({
				...prevErrors,
				refinancePurpose: !value, // Clear error when a selection is made
			}));
		},
		[setFormValue, setFormError, setErrors, clearFormError],
	);

	if (formValues.loanPurpose === LOAN_PURPOSE_BUY_A_HOME) {
		// Buy a Home
		return (
			<div className="jF-space-y-4 jF-m-2 jF-pb-4">
				{/* Property Purchase Stage */}

				{enabledFields.purchaseStage?.length != 0 && (
					<RadioGroupField
						id="propertyStage"
						label="What is your home buying stage?"
						value={purchaseStage || ""}
						options={enabledFields.purchaseStage.map((item) => ({
							id: item.id,
							value: item.value,
							label: item.value,
						}))}
						onChange={(value) => {
							handlePurchaseStageChange(value);
						}}
						required={true}
						error={errors.purchaseStage ? "This field is required" : ""}
						disabled={false}
						className={""}
						gridCols={2}
						showIcons={false}
						disabledOptions={[]}
						navRef={isNavDivSmall}
					/>
				)}

				{/* Property Type */}
				{enabledFields.assetType?.length != 0 && (
					<RadioGroupField
						id="assetType"
						label="What kind of property are you looking to buy?"
						value={assetType || ""}
						options={enabledFields.assetType.map((item) => ({
							id: item.id,
							value: item.value,
							label: item.value,
						}))}
						onChange={(value) => {
							handleAssetTypeChange(value);
						}}
						required={true}
						error={errors.assetType ? "This field is required" : ""}
						disabled={false}
						className={""}
						gridCols={3}
						showIcons={false}
						disabledOptions={[]}
						navRef={isNavDivSmall}
					/>
				)}
			</div>
		);
	} else {
		// Refinancing Mortgage
		return (
			<div className="jF-space-y-4 jF-m-2 jF-pb-4">
				{/* Refinance Purpose */}
				{enabledFields.refinancePurpose?.length != 0 && (
					<RadioGroupField
						id="refinancePurpose"
						label="What is your refinance goal?"
						value={refinancePurpose || ""}
						options={enabledFields.refinancePurpose.map((item) => ({
							id: item.id,
							value: item.value,
							label: item.value,
						}))}
						onChange={(value) => {
							handleRefinancePurposeChange(value);
						}}
						required={true}
						error={errors.refinancePurpose ? "This field is required" : ""}
						disabled={false}
						className={""}
						gridCols={2}
						showIcons={false}
						disabledOptions={[]}
						navRef={isNavDivSmall}
					/>
				)}

				{/* Property Purpose */}

				{enabledFields.propertyPurpose?.length != 0 && (
					<RadioGroupField
						id="propertyPurpose"
						label="What is the property purpose?"
						value={propertyPurpose || ""}
						options={enabledFields.propertyPurpose.map((item) => ({
							id: item.id,
							value: item.value,
							label: item.value,
						}))}
						onChange={(value) => {
							handlePropertyPurposeChange(value);
						}}
						required={true}
						error={errors.propertyPurpose ? "This field is required" : ""}
						disabled={false}
						className={""}
						gridCols={2}
						showIcons={false}
						disabledOptions={[]}
						navRef={isNavDivSmall}
					/>
				)}
			</div>
		);
	}
};
